<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import {
    ColoredButton,
    FieldSearchBar,
    PaginateOrScroll,
    SearchToolbar,
    SelectInput,
  } from '@/shared/ui';
  import { useUserStore } from '@/entities/User';
  import { QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
  import PencilIcon from '@/shared/assets/icons/PencilIcon.svg';
  import RegistrationPaperIcon from '@/shared/assets/icons/RegistrationPaper.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  import RecordListTable from './RecordListTable.vue';
  import { buildQuantityOptions } from '../lib/quantity-options';
  import { RECORD_SEARCH_FIELDS } from '../lib/record-list-query';
  import { useRecordList } from '../model/useRecordList';
  import { useRecordListExport } from '../model/useRecordListExport';
  import { useRecordListActions } from '../model/useRecordListActions';
  import type { RecordListRow } from '../model/records.types';

  const RECORD_LIST_TAB = 'lista-registros';

  const {
    searchTerm,
    searchFieldId,
    sort,
    order,
    filters,
    items,
    allItems,
    metadata,
    loading,
    initializing,
    loadingMore,
    hasMoreItems,
    selectedQuantity,
    selectedIds,
    selectedRevisionIds,
    expandedIds,
    allSelected,
    isIndeterminate,
    toggleSelection,
    toggleRevisionSelection,
    toggleAll,
    toggleExpansion,
    getRevisionsState,
    retryRevisions,
    hasActiveSearch,
    isFilterActive,
    openFilterModal,
    handleSearch,
    handleQuantityChange,
    handlePageChanged,
    handleSortUpdate,
    applyRecordStatus,
    settleRecordStatus,
    initialize,
    refresh,
  } = useRecordList();

  const {
    openMenuKey,
    setOpenMenuKey,
    openEditHomologation,
    openRecordEditor,
    openRecordConfig,
    openHistory,
    openControl,
    openDeleteConfirmation,
    openBulkDeleteConfirmation,
    toggleRecordActive,
    isToggling,
  } = useRecordListActions({
    selectedRecordIds: () => [...selectedIds.value],
    selectedRevisionIds: () => [...selectedRevisionIds.value],
    refresh,
    applyRecordStatus,
    settleRecordStatus,
  });

  const { handleExportToExcel, isExporting, isExportDisabled } = useRecordListExport({
    searchTerm,
    searchFieldId,
    sort,
    order,
    filters,
    metadata,
  });

  const quantityOptions = computed(() =>
    buildQuantityOptions(QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY, RECORD_LIST_TAB)
  );

  /**
   * Inactivating a record is a SUPERUSER action, so for everyone else the switch
   * is not rendered. The check is the role and not a CASL ability, because a
   * superuser holds `manage all`: `can()` could not tell a superuser apart from a
   * well permissioned user. Same reason the "Superadmin" switch of Criar/Editar
   * Usuário reads the role directly.
   */
  const userStore = useUserStore();

  const showInactivateToggle = computed(() => userStore.isSuperuser);

  onMounted(initialize);
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-wrap items-center gap-4">
      <ColoredButton
        text="Em preenchimento"
        :icon="PencilIcon"
        icon-alt="Em preenchimento"
        class="w-fit"
      />
      <ColoredButton
        text="Lista Mestra RQ"
        :icon="RegistrationPaperIcon"
        icon-alt="Lista Mestra RQ"
        class="w-fit"
      />
      <ColoredButton
        text="Exportar para Excel"
        :icon="ExportExcelIcon"
        icon-alt="Exportar para Excel"
        class="w-fit"
        :disabled="isExportDisabled"
        :loading="isExporting"
        @click="handleExportToExcel"
      />
    </div>

    <SearchToolbar>
      <template #search>
        <FieldSearchBar
          v-model:search-field-id="searchFieldId"
          :search-fields="RECORD_SEARCH_FIELDS"
          :initial-search-term="searchTerm"
          independent-search-field
          :clear-search-on-field-change="false"
          @search="handleSearch"
        />
      </template>
      <template #quantity>
        <SelectInput
          :model-value="selectedQuantity"
          :options="quantityOptions"
          class="cursor-pointer"
          @update:model-value="handleQuantityChange"
        />
      </template>
    </SearchToolbar>

    <RecordListTable
      v-if="initializing"
      :records="[]"
      loading
    />

    <PaginateOrScroll
      v-else
      :items="items"
      :all-items="allItems"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      :loading-more="loadingMore"
      :has-more-items="hasMoreItems"
      entity-name="registro"
      @page-changed="handlePageChanged"
    >
      <template #table="{ items: visibleItems }">
        <RecordListTable
          :records="visibleItems as RecordListRow[]"
          :loading="loading"
          :selected-ids="selectedIds"
          :selected-revision-ids="selectedRevisionIds"
          :expanded-ids="expandedIds"
          :all-selected="allSelected"
          :partially-selected="isIndeterminate"
          :has-active-search="hasActiveSearch"
          :is-filter-active="isFilterActive"
          :current-sort="sort"
          :current-order="order"
          :open-menu-key="openMenuKey"
          :show-inactivate-toggle="showInactivateToggle"
          :revisions-state="getRevisionsState"
          :is-toggling="isToggling"
          @toggle-all="toggleAll"
          @toggle-selection="toggleSelection"
          @toggle-revision-selection="toggleRevisionSelection"
          @toggle-expand="toggleExpansion"
          @retry-revisions="retryRevisions"
          @update:sort="handleSortUpdate"
          @filter-clicked="openFilterModal"
          @update:open-menu-key="setOpenMenuKey"
          @open-history="openHistory"
          @open-control="openControl"
          @edit="openRecordEditor"
          @configure="openRecordConfig"
          @edit-homologation="openEditHomologation"
          @delete="openDeleteConfirmation"
          @bulk-delete="openBulkDeleteConfirmation"
          @toggle-active="toggleRecordActive"
        />
      </template>
    </PaginateOrScroll>
  </div>
</template>
